// ═══════════════════════════════════════════════════════════════
// SHELTER MODULE — Shelters CRUD
// Shelter facility management with capacity, ADA, pet-friendly
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Switch, Checkbox,
  NumberInput, Drawer, Divider, Progress,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { Shelter, ShelterStatus } from '../../types/shelter';
import {
  SHELTER_STATUS_COLORS, SHELTER_TYPES, SHELTER_TYPE_LABELS, FACILITY_OPTIONS,
} from '../../types/shelter';
import { formatPhone } from '../../lib/phoneFormat';

const STATUS_OPTIONS: { value: ShelterStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'at_capacity', label: 'At Capacity' },
  { value: 'standby', label: 'Standby' },
];

const TYPE_OPTIONS = SHELTER_TYPES.map(t => ({ value: t, label: SHELTER_TYPE_LABELS[t] ?? t }));

const EMPTY: Partial<Shelter> = {
  name: '', address: '', latitude: '', longitude: '', capacity: 0,
  currentPopulation: 0, isAda: false, petFriendly: false, status: 'standby',
  shelterType: '', facilities: '[]', contactName: '', contactPhone: '',
  contactEmail: '', operatingOrg: '', openedAt: '', closedAt: '', notes: '',
};

export default function Shelters() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    shelters, loadingShelters,
    loadShelters, createShelter, updateShelter, deleteShelter,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Shelter | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shelter | null>(null);
  const [detailTarget, setDetailTarget] = useState<Shelter | null>(null);
  const [form, setForm] = useState<Partial<Shelter>>({ ...EMPTY });
  const [facilities, setFacilities] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => { loadShelters(); }, [loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];

  const filtered = useMemo(() => {
    let list = iShelters;
    if (statusFilter && statusFilter !== 'all') list = list.filter(s => s.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q) ||
        s.operatingOrg.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [iShelters, statusFilter, search]);

  const setField = (key: keyof Shelter, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setFacilities([]);
    setModalOpen(true);
  };

  const openEdit = (s: Shelter) => {
    setEditTarget(s);
    setForm({ ...s });
    try { setFacilities(JSON.parse(s.facilities || '[]')); } catch { setFacilities([]); }
    setModalOpen(true);
  };

  const geocodeAddress = async () => {
    const addr = form.address?.trim();
    if (!addr) { notifications.show({ message: 'Enter an address first', color: 'yellow' }); return; }
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr)}`,
      );
      const data = await res.json();
      if (data && data.length > 0) {
        setForm(prev => ({ ...prev, latitude: data[0].lat, longitude: data[0].lon }));
        notifications.show({ message: 'Coordinates set', color: 'green' });
      } else {
        notifications.show({ message: 'Address not found', color: 'yellow' });
      }
    } catch {
      notifications.show({ message: 'Geocoding failed', color: 'red' });
    } finally { setGeocoding(false); }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Shelter name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        facilities: JSON.stringify(facilities),
        isAda: form.isAda ?? false,
        petFriendly: form.petFriendly ?? false,
      };
      if (editTarget) {
        await updateShelter(editTarget.id, payload);
        notifications.show({ message: 'Shelter updated', color: 'green' });
      } else {
        await createShelter(payload);
        notifications.show({ message: 'Shelter created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteShelter(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const toggleFacility = (val: string) => {
    setFacilities(prev => prev.includes(val) ? prev.filter(f => f !== val) : [...prev, val]);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Shelters</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingShelters} onClick={() => loadShelters()}>Refresh</Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Shelter</Button>
          )}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, address, org..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} shelter{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Address</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Capacity</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Pop.</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Org</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>ADA</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Pets</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingShelters ? 'Loading...' : 'No shelters found'}
                </td></tr>
              )}
              {filtered.map(s => (
                <tr key={s.id} onClick={() => setDetailTarget(s)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{s.name || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{s.address || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" style={{ backgroundColor: SHELTER_STATUS_COLORS[s.status] ?? '#6b7280', color: '#fff' }}>
                      {s.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{s.capacity}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{s.currentPopulation}</td>
                  <td style={{ padding: '8px 12px' }}>{(SHELTER_TYPE_LABELS[s.shelterType] ?? s.shelterType) || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{s.operatingOrg || '-'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    {s.isAda && <Badge size="xs" color="blue">ADA</Badge>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    {s.petFriendly && <Badge size="xs" color="teal">Pets</Badge>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(s)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Shelter' : 'Add Shelter'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Basic Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Shelter Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.currentTarget.value)} />
            <Select label="Type" data={TYPE_OPTIONS} value={form.shelterType ?? ''}
              onChange={v => setField('shelterType', v ?? '')} clearable />
            <TextInput label="Operating Org" value={form.operatingOrg ?? ''}
              onChange={e => setField('operatingOrg', e.currentTarget.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status ?? 'standby'}
              onChange={v => setField('status', v ?? 'standby')} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Location</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Address" value={form.address ?? ''}
              onChange={e => setField('address', e.currentTarget.value)}
              style={{ gridColumn: '1 / -1' }} />
          </SimpleGrid>
          <Group gap="sm">
            <TextInput label="Latitude" value={form.latitude ?? ''}
              onChange={e => setField('latitude', e.currentTarget.value)} style={{ flex: 1 }} />
            <TextInput label="Longitude" value={form.longitude ?? ''}
              onChange={e => setField('longitude', e.currentTarget.value)} style={{ flex: 1 }} />
            <Button size="xs" variant="light" mt={24} loading={geocoding} onClick={geocodeAddress}>
              Geocode
            </Button>
          </Group>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Capacity</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <NumberInput label="Capacity" value={form.capacity ?? 0} min={0}
              onChange={v => setField('capacity', v ?? 0)} />
            <NumberInput label="Current Population" value={form.currentPopulation ?? 0} min={0}
              onChange={v => setField('currentPopulation', v ?? 0)} />
          </SimpleGrid>

          <Group gap="lg" mt="xs">
            <Switch label="ADA Accessible" checked={!!form.isAda}
              onChange={e => setField('isAda', e.currentTarget.checked)} />
            <Switch label="Pet-Friendly" checked={!!form.petFriendly}
              onChange={e => setField('petFriendly', e.currentTarget.checked)} />
          </Group>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Facilities</Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {FACILITY_OPTIONS.map(opt => (
              <Checkbox key={opt.value} label={opt.label}
                checked={facilities.includes(opt.value)}
                onChange={() => toggleFacility(opt.value)} size="xs" />
            ))}
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed" mt="sm">Contact</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Contact Name" value={form.contactName ?? ''}
              onChange={e => setField('contactName', e.currentTarget.value)} />
            <TextInput label="Contact Phone" value={form.contactPhone ?? ''}
              onChange={e => setField('contactPhone', formatPhone(e.currentTarget.value))} />
            <TextInput label="Contact Email" value={form.contactEmail ?? ''}
              onChange={e => setField('contactEmail', e.currentTarget.value)} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Opened At" type="datetime-local"
              value={(form.openedAt || '').replace('Z', '').replace(/:\d{2}$/, '').slice(0, 16)}
              onChange={e => setField('openedAt', e.currentTarget.value)} />
            <TextInput label="Closed At" type="datetime-local"
              value={(form.closedAt || '').replace('Z', '').replace(/:\d{2}$/, '').slice(0, 16)}
              onChange={e => setField('closedAt', e.currentTarget.value)} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Shelter'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Shelter" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)} title="Shelter Details"
        position="right" size="md">
        {detailTarget && (() => {
          const pct = detailTarget.capacity > 0
            ? Math.round((detailTarget.currentPopulation / detailTarget.capacity) * 100)
            : 0;
          let facilityList: string[] = [];
          try { facilityList = JSON.parse(detailTarget.facilities || '[]'); } catch { /* empty */ }
          return (
            <Stack>
              <Group>
                <Text size="xl" fw={700}>{detailTarget.name || 'Unnamed Shelter'}</Text>
                <Badge style={{ backgroundColor: SHELTER_STATUS_COLORS[detailTarget.status] ?? '#6b7280', color: '#fff' }}>
                  {detailTarget.status.replace('_', ' ')}
                </Badge>
                {detailTarget.shelterType && (
                  <Badge color="violet">{SHELTER_TYPE_LABELS[detailTarget.shelterType] ?? detailTarget.shelterType}</Badge>
                )}
              </Group>

              <Divider />

              <div>
                <Text size="xs" c="dimmed">Address</Text>
                <Text size="sm">{detailTarget.address || '-'}</Text>
              </div>

              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Latitude</Text>
                  <Text size="sm">{detailTarget.latitude || '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Longitude</Text>
                  <Text size="sm">{detailTarget.longitude || '-'}</Text>
                </div>
              </SimpleGrid>

              <Divider />

              <div>
                <Text size="xs" c="dimmed">Capacity</Text>
                <Text size="sm" fw={500}>{detailTarget.currentPopulation} / {detailTarget.capacity}</Text>
                <Progress value={pct} color={pct >= 90 ? 'red' : pct >= 70 ? 'yellow' : 'green'} size="sm" mt={4} />
              </div>

              <SimpleGrid cols={2}>
                <div>
                  {detailTarget.isAda ? <Badge color="blue">ADA Accessible</Badge> : <Text size="sm" c="dimmed">Not ADA</Text>}
                </div>
                <div>
                  {detailTarget.petFriendly ? <Badge color="teal">Pet Friendly</Badge> : <Text size="sm" c="dimmed">No Pets</Text>}
                </div>
              </SimpleGrid>

              {facilityList.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <Text size="xs" c="dimmed">Facilities</Text>
                    <Group gap={4} mt={4}>
                      {facilityList.map(f => {
                        const opt = FACILITY_OPTIONS.find(o => o.value === f);
                        return <Badge key={f} size="sm" variant="light">{opt?.label ?? f}</Badge>;
                      })}
                    </Group>
                  </div>
                </>
              )}

              <Divider />

              <Text size="xs" fw={600} c="dimmed">Contact</Text>
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Name</Text>
                  <Text size="sm">{detailTarget.contactName || '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Phone</Text>
                  <Text size="sm">{detailTarget.contactPhone ? formatPhone(detailTarget.contactPhone) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Email</Text>
                  <Text size="sm">{detailTarget.contactEmail || '-'}</Text>
                </div>
              </SimpleGrid>

              <div>
                <Text size="xs" c="dimmed">Operating Org</Text>
                <Text size="sm">{detailTarget.operatingOrg || '-'}</Text>
              </div>

              <Divider />

              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Opened At</Text>
                  <Text size="sm">{detailTarget.openedAt ? new Date(detailTarget.openedAt).toLocaleString() : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Closed At</Text>
                  <Text size="sm">{detailTarget.closedAt ? new Date(detailTarget.closedAt).toLocaleString() : '-'}</Text>
                </div>
              </SimpleGrid>

              <div>
                <Text size="xs" c="dimmed">Notes</Text>
                <Text size="sm">{detailTarget.notes || '-'}</Text>
              </div>

              <Divider />

              {canEdit && (
                <Group>
                  <Button size="xs" leftSection={<IconPencil size={14} />}
                    onClick={() => { const s = detailTarget; setDetailTarget(null); openEdit(s); }}>
                    Edit
                  </Button>
                  <Button size="xs" color="red" leftSection={<IconTrash size={14} />}
                    onClick={() => { const s = detailTarget; setDetailTarget(null); setDeleteTarget(s); }}>
                    Delete
                  </Button>
                </Group>
              )}
            </Stack>
          );
        })()}
      </Drawer>
    </div>
  );
}
